import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_PAYMENT_TOKENS } from "../../../../queries/GetPaymentTokens";
import {
  PaymentToken,
  PaymentTokensResponse,
} from "../../../../pages/BankCardsPage/BankCardsTypes";

// Kept around for manual testing only — not used as a live fallback.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MOCK_PAYMENT_TOKENS: PaymentToken[] = [
  {
    id: "mock-1",
    token: "mock-token-1",
    cardExpirationMonth: 12,
    cardExpirationYear: 2028,
    lastFourDigits: "5432",
    cardOwnerName: "Jane Doe",
    issuer: "mastercard",
    expired: false,
  },
  {
    id: "mock-2",
    token: "mock-token-2",
    cardExpirationMonth: 3,
    cardExpirationYear: 2024,
    lastFourDigits: "1234",
    cardOwnerName: "John Smith",
    issuer: "visa",
    expired: true,
  },
];

export function useSavedPaymentMethods(enabled: boolean) {
  const { data, loading } = useQuery<PaymentTokensResponse>(
    GET_PAYMENT_TOKENS,
    { fetchPolicy: "cache-and-network", skip: !enabled }
  );

  const paymentTokens = useMemo(() => data?.paymentTokens ?? [], [data]);

  // Only meaningful once we've actually run the query (enabled) and it's settled.
  const hasChecked = enabled && !loading;

  const [selected, setSelectedState] = useState<PaymentToken | null>(null);
  const hasUserSelected = useRef(false);

  const setSelected = (token: PaymentToken) => {
    hasUserSelected.current = true;
    setSelectedState(token);
  };

  // Auto-preselect the first non-expired card, only until the user picks one themselves.
  useEffect(() => {
    if (!paymentTokens.length || hasUserSelected.current) return;

    const preselect = paymentTokens.find((token) => !token.expired);
    if (preselect) {
      setSelectedState(preselect);
    }
  }, [paymentTokens]);

  const [wantsInstallments, setWantsInstallments] = useState(false);
  const [installmentsCount, setInstallmentsCount] = useState<number | null>(
    null
  );

  const isValidContinue = Boolean(
    selected &&
      !selected.expired &&
      (!wantsInstallments || installmentsCount !== null)
  );

  return {
    paymentTokens,
    loading,
    hasChecked,
    selected,
    setSelected,
    wantsInstallments,
    setWantsInstallments,
    installmentsCount,
    setInstallmentsCount,
    isValidContinue,
  };
}
